import React, { useEffect, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { db } from './firebase';
import { doc, getDoc, setDoc, Timestamp } from 'firebase/firestore';
import './Subscription.css';

// Vérification de la clé Stripe
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

// Styles pour CardElement
const cardElementStyles = {
  style: {
    base: {
      fontSize: '16px',
      color: '#424770',
      fontFamily: 'Arial, sans-serif',
      '::placeholder': {
        color: '#aab7c4',
      },
    },
    invalid: {
      color: '#9e2146',
    },
  },
};

const CheckoutForm = ({ user, onSubscriptionUpdate }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [cardComplete, setCardComplete] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements || !cardComplete) {
      return;
    }

    setIsProcessing(true);
    setErrorMessage('');

    const cardElement = elements.getElement(CardElement);

    try {
      const { error: paymentMethodError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: {
          email: user.email,
        },
      });

      if (paymentMethodError) {
        throw new Error(paymentMethodError.message);
      }

      const response = await fetch('/.netlify/functions/create-subscription', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user.uid,
          email: user.email,
          paymentMethodId: paymentMethod.id,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Erreur lors de la création de l'abonnement");
      }

      const subscriptionData = await response.json();

      if (subscriptionData.status === 'incomplete' && subscriptionData.clientSecret) {
        const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(
          subscriptionData.clientSecret,
          {
            payment_method: paymentMethod.id,
          }
        );

        if (confirmError) {
          throw new Error(confirmError.message);
        }

        if (paymentIntent.status === 'succeeded') {
          await setDoc(
            doc(db, 'users', user.uid),
            {
              subscription: {
                status: 'active',
                stripeSubscriptionId: subscriptionData.subscriptionId,
                createdAt: Timestamp.fromDate(new Date()),
              },
            },
            { merge: true }
          );

          onSubscriptionUpdate('active');
        } else {
          throw new Error('Paiement non réussi');
        }
      } else if (subscriptionData.status === 'active') {
        await setDoc(
          doc(db, 'users', user.uid),
          {
            subscription: {
              status: 'active',
              stripeSubscriptionId: subscriptionData.subscriptionId,
              createdAt: Timestamp.fromDate(new Date()),
            },
          },
          { merge: true }
        );

        onSubscriptionUpdate('active');
      } else {
        throw new Error("État de l'abonnement non pris en charge");
      }
    } catch (error) {
      console.error('Erreur lors du paiement:', error);
      setErrorMessage(error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="subscription-form">
      <div className="payment-fields">
        <div className="card-input-container">
          <label>Numéro de carte</label>
          <div className="card-element-wrapper">
            <CardElement
              options={cardElementStyles}
              onChange={(e) => setCardComplete(e.complete)}
            />
          </div>
        </div>
      </div>

      {errorMessage && <div className="error-message">{errorMessage}</div>}

      <button
        type="submit"
        disabled={!stripe || isProcessing || !cardComplete}
        className="subscribe-button"
      >
        {isProcessing ? 'Traitement en cours...' : 'S\'abonner'}
      </button>
    </form>
  );
};

const Subscription = ({ user }) => {
  const [subscriptionStatus, setSubscriptionStatus] = useState('loading');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showSubscribeForm, setShowSubscribeForm] = useState(false);
  const [willEndAt, setWillEndAt] = useState(null);

  useEffect(() => {
    const checkSubscription = async () => {
      if (!user?.uid) return;

      setIsLoading(true);
      try {
        // 1. Vérifier dans Firestore
        const userRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userRef);
        const userData = userDoc.data();

        let newStatus = userData?.subscription?.status || 'not_subscribed';
        let willEndAtDate = null;

        // Débogage de la valeur brute de willEndAt
        console.log('Raw willEndAt:', userData?.subscription?.willEndAt, 'Type:', typeof userData?.subscription?.willEndAt);

        // Vérification et conversion sécurisée de willEndAt
        if (userData?.subscription?.willEndAt && typeof userData.subscription.willEndAt === 'object' && 'seconds' in userData.subscription.willEndAt) {
          willEndAtDate = new Date(userData.subscription.willEndAt.seconds * 1000 + Math.floor(userData.subscription.willEndAt.nanoseconds / 1000000));
        } else if (typeof userData?.subscription?.willEndAt === 'string') {
          willEndAtDate = new Date(userData.subscription.willEndAt);
        } else if (userData?.subscription?.willEndAt) {
          console.warn('willEndAt format inattendu, utilisant la date actuelle comme fallback');
          willEndAtDate = new Date();
        }

        // 2. Forcer la mise à jour si cancelling et date dépassée, ou annulation il y a plus d'un mois
        if (newStatus === 'cancelling' && willEndAtDate) {
          const currentDate = new Date();
          console.log('Current Date:', currentDate, 'willEndAtDate:', willEndAtDate);
          const cancellationDate = userData?.subscription?.cancelledAt instanceof Timestamp 
            ? userData.subscription.cancelledAt.toDate() 
            : new Date(userData?.subscription?.cancelledAt);
          const oneMonthAgo = new Date();
          oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);

          if (currentDate > willEndAtDate || (cancellationDate && cancellationDate < oneMonthAgo)) {
            newStatus = 'not_subscribed';
            await setDoc(
              userRef,
              {
                subscription: {
                  status: 'not_subscribed',
                  updatedAt: Timestamp.fromDate(currentDate),
                  willEndAt: null,
                },
              },
              { merge: true }
            );
            console.log('Statut mis à jour à not_subscribed car willEndAt dépassé ou annulation il y a plus d\'un mois');
          }
        }

        setSubscriptionStatus(newStatus);
        setWillEndAt(willEndAtDate);

        // 3. Vérification avec Stripe si nécessaire
        if (!userData?.subscription?.status || newStatus === 'not_subscribed') {
          const response = await fetch('/.netlify/functions/check-subscription', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ userId: user.uid, email: user.email }),
          });

          if (!response.ok) {
            throw new Error("Erreur lors de la vérification de l'abonnement");
          }

          const data = await response.json();
          const stripeStatus = data.subscriptionStatus || 'not_subscribed';

          if (stripeStatus !== newStatus) {
            await setDoc(
              userRef,
              {
                subscription: {
                  status: stripeStatus,
                  updatedAt: Timestamp.fromDate(new Date()),
                  willEndAt: data.willEndAt ? Timestamp.fromDate(new Date(data.willEndAt)) : null,
                },
              },
              { merge: true }
            );
            setSubscriptionStatus(stripeStatus);
            setWillEndAt(data.willEndAt ? new Date(data.willEndAt) : null);
          }
        }
      } catch (error) {
        console.error('Erreur de vérification d\'abonnement:', error);
        setError(error.message);
        setSubscriptionStatus('error');
      } finally {
        setIsLoading(false);
      }
    };

    checkSubscription();
  }, [user]);

  const handleSubscriptionUpdate = (newStatus) => {
    setSubscriptionStatus(newStatus);
    setShowSubscribeForm(false);
    setWillEndAt(null);
  };

  const handleCancelSubscription = async () => {
    if (!user?.uid) return;

    try {
      const response = await fetch('/.netlify/functions/cancel-subscription', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user.uid }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Erreur lors de l'annulation");
      }

      const data = await response.json();
      console.log('[CancelSubscription] Réponse de l\'API:', data); // Débogage de la réponse

      if (!data.willEndAt) {
        console.error('[CancelSubscription] Aucune date willEndAt dans la réponse de l\'API:', data);
        throw new Error('Aucune date de fin d\'abonnement retournée par l\'API');
      }

      setSubscriptionStatus('cancelling');
      const newWillEndAt = new Date(data.willEndAt);
      setWillEndAt(newWillEndAt);
      console.log('[CancelSubscription] Nouvelle date willEndAt:', newWillEndAt);

      await setDoc(
        doc(db, 'users', user.uid),
        {
          subscription: {
            status: 'cancelling',
            cancelledAt: Timestamp.fromDate(new Date()),
            willEndAt: Timestamp.fromDate(newWillEndAt),
          },
        },
        { merge: true }
      );
      console.log('[CancelSubscription] Mise à jour Firestore réussie avec willEndAt:', newWillEndAt);
    } catch (error) {
      console.error('Erreur lors de l\'annulation:', error);
      setError(error.message);
    }
  };

  const handleReinstateSubscription = async () => {
    if (!user?.uid) return;

    try {
      const response = await fetch('/.netlify/functions/reinstate-subscription', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user.uid }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Erreur lors de la réactivation");
      }

      setSubscriptionStatus('active');
      setWillEndAt(null);

      await setDoc(
        doc(db, 'users', user.uid),
        {
          subscription: {
            status: 'active',
            updatedAt: Timestamp.fromDate(new Date()),
            willEndAt: null,
          },
        },
        { merge: true }
      );
    } catch (error) {
      console.error('Erreur lors de la réactivation:', error);
      setError(error.message);
    }
  };

  if (isLoading) {
    return <div className="subscription-loading">Vérification de votre abonnement...</div>;
  }

  return (
    <div className="subscription-section">
      <h2 className="account-title">Abonnement</h2>
      <div className="subscription-card">
        <div className="subscription-status">
          <h3>
            Statut de l'abonnement :{' '}
            {subscriptionStatus === 'active'
              ? 'Actif'
              : subscriptionStatus === 'cancelling'
              ? 'En cours d\'annulation'
              : subscriptionStatus === 'error'
              ? 'Erreur'
              : 'Non abonné'}
          </h3>
        </div>

        {error && (
          <div className="error-message">
            {error}. Veuillez réessayer plus tard.
          </div>
        )}

        {subscriptionStatus === 'active' && (
          <div className="subscription-active">
            <p>Merci pour votre abonnement ! Vous avez accès à toutes les fonctionnalités premium.</p>
            <button className="cancel-btn" onClick={handleCancelSubscription}>
              Résilier l'abonnement
            </button>
          </div>
        )}

        {subscriptionStatus === 'cancelling' && willEndAt && new Date() <= willEndAt && (
          <div className="subscription-cancelling">
            <p>
              Votre abonnement est en cours d'annulation. Vous aurez accès aux fonctionnalités premium jusqu'au{' '}
              {willEndAt.toLocaleDateString('fr-FR')}.
            </p>
            <button className="reinstate-btn" onClick={handleReinstateSubscription}>
              Prolonger l'abonnement
            </button>
          </div>
        )}

        {subscriptionStatus === 'error' && (
          <div className="subscription-error">
            <p>Une erreur est survenue lors de la vérification de votre abonnement. Veuillez réessayer plus tard.</p>
            <button className="retry-btn" onClick={() => window.location.reload()}>
              Réessayer
            </button>
          </div>
        )}

        {(subscriptionStatus === 'not_subscribed' || showSubscribeForm) && (
          <div className="subscription-inactive">
            {showSubscribeForm ? (
              <>
                <h3>S'abonner au plan Premium</h3>
                <Elements stripe={stripePromise}>
                  <CheckoutForm user={user} onSubscriptionUpdate={handleSubscriptionUpdate} />
                </Elements>
              </>
            ) : (
              <p>Vous n'êtes actuellement pas abonné. Choisissez un plan ci-dessous pour commencer.</p>
            )}
          </div>
        )}
      </div>

      <div className="pricing-plans">
        <div className={`pricing-card ${subscriptionStatus === 'not_subscribed' ? 'highlighted' : ''}`}>
          <h3>Gratuit</h3>
          <p className="price">0 € /mois</p>
          <p className="description">Parfait pour débuter</p>
          <ul className="features-list">
            <li>5 quiz par jour</li>
            <li>5 sessions de flashcards par jour</li>
            <li>Suivi de progression basique</li>
            <li>Support par email</li>
          </ul>
          <button
            className="action-button secondary"
            disabled={subscriptionStatus === 'not_subscribed' || (subscriptionStatus === 'cancelling' && willEndAt && new Date() <= willEndAt)}
          >
            {subscriptionStatus === 'not_subscribed'
              ? 'Votre plan actuel'
              : subscriptionStatus === 'cancelling' && willEndAt && new Date() <= willEndAt
              ? `Votre plan au ${willEndAt.toLocaleDateString('fr-FR')}`
              : 'Choisir'}
          </button>
        </div>
        <div className={`pricing-card popular ${subscriptionStatus === 'active' || (subscriptionStatus === 'cancelling' && willEndAt && new Date() <= willEndAt) ? 'highlighted' : ''}`}>
          <div className={`popular-tag ${subscriptionStatus === 'active' || (subscriptionStatus === 'cancelling' && willEndAt && new Date() <= willEndAt) ? 'active-tag' : ''}`}>
            {subscriptionStatus === 'active' || (subscriptionStatus === 'cancelling' && willEndAt && new Date() <= willEndAt) ? 'Actif' : 'Populaire'}
          </div>
          <h3>Premium</h3>
          <p className="price">2 € /mois</p>
          <p className="description">Pour les apprenants sérieux</p>
          <ul className="features-list">
            <li>Quiz illimités</li>
            <li>Sessions de flashcards illimités</li>
            <li>Analytique avancée</li>
            <li>Support prioritaire</li>
            <li>Thèmes personnalisés</li>
          </ul>
          <button
            className="action-button primary"
            onClick={() => setShowSubscribeForm(true)}
            disabled={subscriptionStatus === 'active' || (subscriptionStatus === 'cancelling' && willEndAt && new Date() <= willEndAt)}
          >
            {subscriptionStatus === 'active' || (subscriptionStatus === 'cancelling' && willEndAt && new Date() <= willEndAt) ? 'Votre plan actuel' : 'S\'abonner'}
          </button>
        </div>
        <div className="pricing-card">
          <h3>Personnalisé</h3>
          <p className="price">Sur Devis</p>
          <p className="description">Adapté aux institutions</p>
          <ul className="features-list">
            <li>Création de quiz sur mesure</li>
            <li>Support dédié</li>
            <li>Gestion d'équipe</li>
            <li>Intégrations avancées</li>
            <li>Sessions de formation</li>
          </ul>
          <a href="/contact" className="action-button secondary">
            Contacter-Nous
          </a>
        </div>
      </div>
    </div>
  );
};

export default Subscription;